import { useState } from "react";
import { cs } from "@/i18n/cs";
import { closeJarvis, useJarvisState } from "./jarvis-store";
import { useJarvisChat } from "./useJarvisChat";

/**
 * Desktop Jarvis overlay (spec §64–71, /Design „JARVIS VOICE MODE"): orb
 * vpravo dole, konverzace, dopad provedený Factory, risk confirm a navigace.
 * Sdílenou logiku drží useJarvisChat (společná s mobilní stránkou).
 */
export function JarvisOverlay() {
  const { open, prefill } = useJarvisState();
  if (!open) return null;
  return <JarvisPanel prefill={prefill} />;
}

function JarvisPanel({ prefill }: { prefill: string | null }) {
  const jarvis = useJarvisChat();
  const [input, setInput] = useState(prefill ?? "");
  const reply = jarvis.exchange?.reply ?? null;

  const send = (text: string) => {
    if (!text.trim()) return;
    setInput("");
    jarvis.send(text);
  };
  const endSession = () => {
    jarvis.stop();
    closeJarvis();
  };

  return (
    <div
      className="af-animate-fade fixed inset-x-2 bottom-4 z-50 flex flex-col items-end gap-3.5 md:inset-x-auto md:bottom-6 md:right-6 md:max-w-[480px]"
      data-testid="jarvis-overlay"
    >
      {/* stavový řádek */}
      <div className="flex items-center gap-2.5 text-[11px] tracking-[0.2em]">
        {jarvis.recording ? (
          <span className="af-animate-pulse" style={{ color: "var(--accent)" }}>
            ● {cs.jarvis.listening}
          </span>
        ) : jarvis.voiceOutput ? (
          <span style={{ color: "var(--accent)" }}>● {cs.jarvis.voiceActive}</span>
        ) : (
          <span style={{ color: jarvis.online ? "var(--accent)" : "var(--critical)" }}>
            {jarvis.online ? `● JARVIS · ${jarvis.model}` : cs.jarvis.notConfigured}
          </span>
        )}
        <button
          type="button"
          className="cursor-pointer rounded-full px-3 py-1"
          style={{ borderRadius: "var(--radius-box)",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-default)",
            background: "var(--surface-overlay)",
          }}
          onClick={endSession}
          data-testid="jarvis-close"
        >
          ✕ {cs.jarvis.end}
        </button>
      </div>

      {/* konverzační panel */}
      <div
        className="w-full p-4 md:w-[440px]"
        style={{ borderRadius: "var(--radius-box)",
          border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)",
          background: "var(--surface-overlay)",
          boxShadow: "0 0 40px color-mix(in srgb, var(--accent) 12%, transparent)",
          backdropFilter: "blur(4px)",
        }}
      >
        {!jarvis.exchange ? (
          <>
            <div className="mb-3 text-[11px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
              {jarvis.online ? cs.jarvis.intro : cs.jarvis.offlineIntro}
            </div>
            {jarvis.online ? (
              <div className="flex flex-col gap-1.5">
                {cs.jarvis.suggestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="cursor-pointer px-3 py-2 text-left text-[12px]"
                    style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--accent) 20%, transparent)", color: "var(--accent)" }}
                    onClick={() => send(q)}
                    data-testid="jarvis-suggestion"
                  >
                    „{q}"
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="mb-2.5 flex gap-2.5">
              <span className="flex-none pt-0.5 text-[11px] tracking-[0.14em]" style={{ color: "var(--text-tertiary)" }}>
                TY
              </span>
              <span className="text-[11px] leading-relaxed" style={{ color: "var(--text-primary)" }}>
                „{jarvis.exchange.user}"
              </span>
            </div>
            <div className="mb-2.5 flex gap-2.5">
              <span className="flex-none pt-0.5 text-[11px] tracking-[0.14em]" style={{ color: "var(--accent)" }}>
                J
              </span>
              <span className="text-[11px] leading-[1.65]" style={{ color: "var(--text-primary)" }} data-testid="jarvis-reply">
                {reply ? reply.reply : <span className="af-animate-pulse">{cs.jarvis.thinking}</span>}
              </span>
            </div>

            {jarvis.speechBlocked ? (
              <button
                type="button"
                className="mb-2.5 cursor-pointer px-3 py-1.5 text-[11px] tracking-[0.1em]"
                style={{ borderRadius: "var(--radius-box)", color: "var(--accent)", border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)" }}
                title={cs.jarvis.playBlockedNote}
                onClick={jarvis.playBlockedSpeech}
                data-testid="jarvis-play-blocked"
              >
                {cs.jarvis.playBlocked}
              </button>
            ) : null}

            {reply && reply.impact.length > 0 ? (
              <div
                className="mb-2.5 px-3 py-2"
                style={{ borderRadius: "var(--radius-box)", border: "1px solid color-mix(in srgb, var(--positive) 25%, transparent)", background: "color-mix(in srgb, var(--positive) 3%, transparent)" }}
                data-testid="jarvis-impact"
              >
                <div className="mb-1 text-[11px] tracking-[0.16em]" style={{ color: "var(--positive)" }}>
                  {cs.jarvis.impact}
                </div>
                {reply.impact.map((line, i) => (
                  <div key={i} className="py-0.5 text-[11px] leading-normal" style={{ color: "var(--text-primary)" }}>
                    <span style={{ color: "var(--positive)" }}>→</span> {line}
                  </div>
                ))}
              </div>
            ) : null}

            <div className="flex items-center justify-between gap-2">
              {reply?.chip ? (
                <span
                  className="px-2.5 py-1 text-[11px] tracking-[0.1em]"
                  style={{ borderRadius: "var(--radius-box)",
                    color: reply.pendingAction ? "var(--critical)" : "var(--accent)",
                    border: `1px solid ${reply.pendingAction ? "color-mix(in srgb, var(--critical) 45%, transparent)" : "color-mix(in srgb, var(--accent) 30%, transparent)"}`,
                  }}
                  data-testid="jarvis-chip"
                >
                  {reply.chip}
                </span>
              ) : (
                <span />
              )}
              {reply?.pendingAction ? (
                <button
                  type="button"
                  className="cursor-pointer px-3.5 py-1.5 text-[11px] font-bold tracking-[0.08em]"
                  style={{ background: "var(--critical)", color: "var(--text-on-accent)" }}
                  disabled={jarvis.confirmPending}
                  onClick={() => jarvis.confirm(reply.pendingAction!)}
                  data-testid="jarvis-confirm"
                >
                  {reply.pendingAction.confirmLabel}
                </button>
              ) : null}
            </div>
          </>
        )}

        {jarvis.error ? (
          <div className="mt-2 text-[11px]" style={{ color: "var(--critical)" }}>
            ⚠ {jarvis.error}
          </div>
        ) : null}

        {/* vstup */}
        {jarvis.online ? (
          <div className="mt-3 flex items-center gap-2 pt-2.5" style={{ borderTop: "1px solid color-mix(in srgb, var(--accent) 12%, transparent)" }}>
            <input
              className="flex-1 bg-transparent font-mono text-[11px] outline-none"
              style={{ color: "var(--text-primary)" }}
              placeholder={cs.jarvis.placeholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send(input);
              }}
              autoFocus
              data-testid="jarvis-input"
            />
            {jarvis.voiceAvailable ? (
              <>
                <button
                  type="button"
                  className="cursor-pointer px-2 py-1 text-[11px] tracking-[0.1em]"
                  style={{ borderRadius: "var(--radius-box)",
                    color: jarvis.voiceOutput ? "var(--accent)" : "var(--text-secondary)",
                    border: `1px solid ${jarvis.voiceOutput ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                  }}
                  title={jarvis.voiceOutput ? cs.jarvis.voiceOutputOn : cs.jarvis.voiceOutputOff}
                  onClick={() => jarvis.setVoiceOutput(!jarvis.voiceOutput)}
                  data-testid="jarvis-voice-output"
                >
                  {jarvis.voiceOutput ? "🔊" : "🔇"}
                </button>
                <button
                  type="button"
                  className="cursor-pointer px-2 py-1 text-[11px] tracking-[0.1em]"
                  style={{ borderRadius: "var(--radius-box)",
                    color: jarvis.recording ? "var(--accent)" : "var(--text-secondary)",
                    border: `1px solid ${jarvis.recording ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--border-default)"}`,
                    background: jarvis.recording ? "color-mix(in srgb, var(--accent) 12%, transparent)" : "transparent",
                  }}
                  title={cs.jarvis.voiceToggle}
                  onClick={() => void jarvis.toggleRecording()}
                  data-testid="jarvis-mic"
                >
                  {jarvis.recording ? "■" : "🎙"}
                </button>
              </>
            ) : null}
            <button
              type="button"
              className="cursor-pointer px-2.5 py-1 text-[12px]"
              style={{ borderRadius: "var(--radius-box)", color: "var(--accent)", border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)" }}
              onClick={() => send(input)}
              disabled={jarvis.pending}
              data-testid="jarvis-send"
            >
              →
            </button>
          </div>
        ) : null}
      </div>

      {/* 3D orb dle /Design */}
      <div className="relative mr-1.5 h-[110px] w-[110px]">
        <div
          className="af-orb-core absolute rounded-full"
          style={{
            inset: 13,
            background: "radial-gradient(circle at 34% 28%, #c9f7ff 0%, #4fdcf2 22%, #0e7f96 55%, #032b34 85%)",
          }}
        />
        <svg width="110" height="110" viewBox="0 0 120 120" className="af-orb-ring absolute inset-0">
          <ellipse cx="60" cy="60" rx="56" ry="20" fill="none" stroke="color-mix(in srgb, var(--accent) 40%, transparent)" strokeWidth="1" strokeDasharray="6 10" />
        </svg>
        <svg width="110" height="110" viewBox="0 0 120 120" className="af-orb-ring-r absolute inset-0">
          <ellipse cx="60" cy="60" rx="20" ry="56" fill="none" stroke="color-mix(in srgb, var(--accent) 25%, transparent)" strokeWidth="1" strokeDasharray="4 12" />
        </svg>
        {(jarvis.pending || jarvis.recording) && (
          <div className="absolute -bottom-1 left-1/2 flex h-3 -translate-x-1/2 items-end gap-[2px]">
            {[7, 12, 5, 10, 7].map((h, i) => (
              <div
                key={i}
                className="af-orb-bar w-[2px]"
                style={{ height: h, background: "var(--accent)", animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
